import React, { useState } from "react";
import useCommunityStats from "../hooks/useCommunityStats";
import { Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Container,
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Avatar,
  Chip,
  Grid,
  Card,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import AutoGraphRoundedIcon from "@mui/icons-material/AutoGraphRounded";
import BalanceRoundedIcon from "@mui/icons-material/BalanceRounded";
import HandshakeRoundedIcon from "@mui/icons-material/HandshakeRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import StarsRoundedIcon from "@mui/icons-material/StarsRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import ProtectedCertificateModal from "../components/ProtectedCertificateModal";

export default function MutualTrustHistory() {
  const communityStats = useCommunityStats();
  const theme = useTheme();
  const [copiedAccount, setCopiedAccount] = useState("");
  const [certOpen, setCertOpen] = useState(false);

  const handleCopy = (acctNumber) => {
    navigator.clipboard.writeText(acctNumber);
    setCopiedAccount(acctNumber);
    setTimeout(() => setCopiedAccount(""), 2500);
  };

  const milestones = [
    {
      year: "2007",
      title: "Government Registration & Foundation",
      category: "Legal Incorporation",
      color: "#059669",
      icon: <VerifiedRoundedIcon sx={{ fontSize: 24 }} />,
      desc: "Mutual Trust Adigbe (Obafemi/Owode) 'FUG' Cooperative Multipurpose Society Limited was officially registered on 25th October 2007 under Section 7(1) of the Co-operative Societies Law (Cap. 26 of the Ogun State Laws of 1978, Reg No. 7425). Formed in Abeokuta by founding members committed to interest-free Islamic cooperation, the society established a trusted, self-sustaining financial community.",
      highlights: [
        "Ogun State Government Registration No. 7425",
        "Registered under Co-operative Societies Law Cap. 26",
        "Foundational commitment to non-interest Islamic finance",
      ],
      hasCertificate: true,
    },
    {
      year: "2024",
      title: "Strategic Migration to Jaiz Bank",
      category: "Banking Alignment",
      color: "#0B3B60",
      icon: <AccountBalanceRoundedIcon sx={{ fontSize: 24 }} />,
      desc: "To guarantee 100% adherence to non-interest banking principles, the Society transitioned all custodial banking relationships from Access Bank to Jaiz Bank Plc — Nigeria's premier non-interest commercial bank. Four specialized institutional accounts were established to segregate society operations, trade capital, member welfare, and targeted goal savings.",
      highlights: [
        "Full migration to licensed non-interest banking",
        "Dedicated institutional account segmentation",
        "Enhanced custodian security for member deposits",
      ],
    },
    {
      year: "2025",
      title: "Regional Branch Expansion",
      category: "Strategic Growth",
      color: "#D97706",
      icon: <AutoGraphRoundedIcon sx={{ fontSize: 24 }} />,
      desc: "Driven by rising demand from across Ogun State and neighboring regions, Mutual Trust initiated the establishment of two regional branches. This physical expansion brings cooperative administrative services, passbook verification, and loan processing closer to members and their local commercial enterprises.",
      highlights: [
        "Establishment of two regional branch offices",
        "Decentralized service delivery for local enterprises",
        "Closer proximity for member inquiries and services",
      ],
    },
    {
      year: "2025",
      title: `Membership Strength Reaches ${communityStats.activeMembers}+ Members`,
      category: "Demographics",
      color: "#7C3AED",
      icon: <GroupsRoundedIcon sx={{ fontSize: 24 }} />,
      desc: `By 2025–2026, Mutual Trust had grown into a thriving network of over ${communityStats.activeMembers} active members representing diverse socioeconomic spheres. Our membership comprises university academics, state and federal civil servants, public sector professionals, skilled artisans, and thriving small-business entrepreneurs.`,
      highlights: [
        "Cross-sector community: civil servants, artisans, academics",
        "Culture of mutual accountability and zero defaults",
        "Democratic member voting with Shura Committee candidate screening",
      ],
    },
    {
      year: "2026",
      title: "MTC Member Web Portal",
      category: "Digital Record Keeping",
      color: "#0284C7",
      icon: <SecurityRoundedIcon sx={{ fontSize: 24 }} />,
      desc: "To strengthen operational transparency and simplify record management, the society deployed the MTC web portal — enabling members to view their savings ledgers, shares, and loan balances securely while facilitating accurate administrative reconciliation across all nine cooperative schemes.",
      highlights: [
        "Secure web portal for member balance inquiries and account history",
        "Accurate digital record keeping across 9 specialized schemes",
        "Transparent account reconciliations and ledger tracking",
      ],
    },
    {
      year: "2026+",
      title: "Financial Inclusion & Economic Empowerment",
      category: "Vision Ahead",
      color: "#10B981",
      icon: <StarsRoundedIcon sx={{ fontSize: 24 }} />,
      desc: "Looking to the future, Mutual Trust continues to expand non-interest commercial procurement partnerships, entrepreneurship literacy, cooperative land development schemes, and family takaful solidarity — empowering everyday households to achieve generational prosperity without usury.",
      highlights: [
        "Expanded commercial commodity procurement partnerships",
        "Community micro-enterprise seed capital",
        "Generational wealth creation and financial literacy",
      ],
    },
  ];

  const pillars = [
    {
      title: "Non-Interest (Riba-Free)",
      icon: <BalanceRoundedIcon sx={{ fontSize: 32, color: "#059669" }} />,
      desc: "Strict adherence to non-interest financial principles across savings, lending, and business partnerships in accordance with Islamic injunctions.",
    },
    {
      title: "Collective Surplus Sharing",
      icon: <StarsRoundedIcon sx={{ fontSize: 32, color: "#F59E0B" }} />,
      desc: "Profits realized from cooperative business purchases and trade investments are distributed equitably among members based on their share capital participation.",
    },
    {
      title: "Democratic Governance & Shura",
      icon: <GroupsRoundedIcon sx={{ fontSize: 32, color: "#0B3B60" }} />,
      desc: "One member, one vote. A dedicated Shura Committee screens and shortlists leadership candidates from the open nominations submitted by all members, while executive officers and all key societal decisions are voted upon directly by all members with strict democratic audit and officer oversight.",
    },
    {
      title: "Mutual Solidarity (Takaful)",
      icon: <HandshakeRoundedIcon sx={{ fontSize: 32, color: "#7C3AED" }} />,
      desc: "Shared mutual risk pooling and emergency welfare support ensuring no member walks alone in times of medical distress, bereavement, or sudden hardship.",
    },
  ];

  const jaizAccounts = [
    { title: "Main Operations Account", number: "0018169614", category: "Core Operations" },
    { title: "Business Partnership Account", number: "0018169676", category: "Trade & Commerce" },
    { title: "Welfare & Emergency Relief", number: "0018169731", category: "Member Welfare" },
    { title: "Target Savings Account", number: "0018169803", category: "Goal Savings" },
  ];

  return (
    <>
      <Helmet>
        <title>Our Story & Milestones | Mutual Trust Cooperative Multipurpose Society</title>
      </Helmet>

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: theme.palette.mode === "dark" ? "#061523" : "#F8FAFC",
          color: theme.palette.mode === "dark" ? "#F1F5F9" : "#0F172A",
          overflowX: "hidden",
        }}
      >
        {/* Sticky Header / Navbar */}
        <Box
          component="nav"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1100,
            backdropFilter: "blur(14px)",
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(6, 21, 35, 0.88)"
                : "rgba(255, 255, 255, 0.92)",
            borderBottom: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(11, 59, 96, 0.08)"
            }`,
            py: 1.5,
          }}
        >
          <Container maxWidth="lg">
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack
                component={RouterLink}
                to="/"
                direction="row"
                alignItems="center"
                spacing={1.75}
                sx={{ textDecoration: "none", color: "inherit" }}
              >
                <Avatar
                  src="/assets/icons/mtc.png"
                  alt="Mutual Trust Logo"
                  sx={{
                    width: 44,
                    height: 44,
                    bgcolor: "transparent",
                    p: 0.25,
                    border: `1.5px solid ${alpha("#059669", 0.4)}`,
                    boxShadow: "0 4px 12px rgba(5, 150, 105, 0.15)",
                  }}
                />
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 800,
                      lineHeight: 1.15,
                      letterSpacing: -0.2,
                      color: theme.palette.mode === "dark" ? "#FFFFFF" : "#0B3B60",
                      fontSize: { xs: 14, sm: 16 },
                    }}
                  >
                    Mutual Trust CMS
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "#059669",
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                      fontSize: 10,
                    }}
                  >
                    Our History & Milestones
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button
                  component={RouterLink}
                  to="/"
                  startIcon={<HomeRoundedIcon />}
                  variant="text"
                  sx={{
                    color: theme.palette.mode === "dark" ? "#E2E8F0" : "#334155",
                    fontWeight: 600,
                    fontSize: 13,
                    display: { xs: "none", sm: "inline-flex" },
                    "&:hover": { bgcolor: alpha("#0B3B60", 0.08) },
                  }}
                >
                  Home
                </Button>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="contained"
                  endIcon={<LoginRoundedIcon />}
                  sx={{
                    bgcolor: "#0B3B60",
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: 13,
                    px: { xs: 2, sm: 3 },
                    py: 0.9,
                    borderRadius: 2,
                    boxShadow: "0 6px 18px rgba(11, 59, 96, 0.28)",
                    "&:hover": {
                      bgcolor: "#082942",
                      boxShadow: "0 8px 22px rgba(11, 59, 96, 0.38)",
                    },
                  }}
                >
                  Sign In
                </Button>
              </Stack>
            </Stack>
          </Container>
        </Box>

        {/* Hero Section */}
        <Box
          sx={{
            position: "relative",
            py: { xs: 6, md: 9 },
            background:
              theme.palette.mode === "dark"
                ? "radial-gradient(ellipse at 50% 0%, rgba(5, 150, 105, 0.22) 0%, rgba(11, 59, 96, 0.45) 50%, #061523 100%)"
                : "radial-gradient(ellipse at 50% 0%, rgba(5, 150, 105, 0.12) 0%, rgba(11, 59, 96, 0.1) 45%, #F8FAFC 100%)",
            borderBottom: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.06)"
                : "rgba(11, 59, 96, 0.06)"
            }`,
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ maxWidth: 860, mx: "auto", textAlign: "center" }}>
              <Button
                component={RouterLink}
                to="/"
                startIcon={<ArrowBackRoundedIcon />}
                size="small"
                sx={{
                  mb: 3,
                  color: "#059669",
                  fontWeight: 700,
                  fontSize: 12,
                  bgcolor: alpha("#059669", 0.08),
                  px: 2,
                  py: 0.6,
                  borderRadius: 6,
                  "&:hover": { bgcolor: alpha("#059669", 0.16) },
                }}
              >
                Back to Homepage
              </Button>

              <Box sx={{ display: "inline-block", position: "relative", mb: 2.5 }}>
                <Avatar
                  src="/assets/icons/mtc.png"
                  alt="Mutual Trust CMS"
                  sx={{
                    width: { xs: 80, sm: 96 },
                    height: { xs: 80, sm: 96 },
                    bgcolor: "transparent",
                    mx: "auto",
                    boxShadow: "0 14px 34px rgba(11, 59, 96, 0.35)",
                    border: "3px solid #059669",
                    p: 0.5,
                  }}
                />
              </Box>

              <Typography
                component="h1"
                variant="h2"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: 26, sm: 36, md: 44 },
                  letterSpacing: -0.6,
                  lineHeight: 1.15,
                  color: theme.palette.mode === "dark" ? "#FFFFFF" : "#0B3B60",
                  mb: 2,
                }}
              >
                Mutual Trust Cooperative Multipurpose Society
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: 15, sm: 18 },
                  color:
                    theme.palette.mode === "dark"
                      ? "rgba(226, 232, 240, 0.85)"
                      : "rgba(51, 65, 85, 0.9)",
                  lineHeight: 1.6,
                  maxWidth: 720,
                  mx: "auto",
                  mb: 3.5,
                }}
              >
                Nearly two decades of principled, non-interest financial stewardship, democratic
                solidarity, and collective community empowerment in Abeokuta, Ogun State.
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 3 }}
              >
                <Chip
                  label="Registered Cooperative Society"
                  icon={<VerifiedRoundedIcon sx={{ fontSize: 16, color: "#059669 !important" }} />}
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    bgcolor: alpha("#059669", 0.12),
                    color: "#059669",
                    border: `1px solid ${alpha("#059669", 0.25)}`,
                  }}
                />
                <Chip
                  label="Founded 2007"
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    bgcolor: alpha("#0B3B60", 0.1),
                    color: theme.palette.mode === "dark" ? "#93C5FD" : "#0B3B60",
                  }}
                />
                <Chip
                  label={communityStats.displayText}
                  icon={<GroupsRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    bgcolor: alpha("#7C3AED", 0.1),
                    color: "#7C3AED",
                  }}
                />
                <Chip
                  label="100% Non-Interest"
                  icon={<BalanceRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    bgcolor: alpha("#D97706", 0.1),
                    color: "#D97706",
                  }}
                />
              </Stack>

              {/* Secretariat Contact Box */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 3,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.04)"
                      : "rgba(255, 255, 255, 0.8)",
                  border: `1px solid ${
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(11, 59, 96, 0.1)"
                  }`,
                  flexWrap: "wrap",
                  justifyContent: "center",
                  boxShadow: "0 4px 18px rgba(0, 0, 0, 0.04)",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <LocationOnRoundedIcon sx={{ fontSize: 18, color: "#059669" }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: 12, sm: 13 } }}>
                    Permanent Site, Odofin Via Somorin Obantoko, Abeokuta, Ogun State
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    bgcolor: "text.disabled",
                    display: { xs: "none", sm: "block" },
                  }}
                />
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <PhoneInTalkRoundedIcon sx={{ fontSize: 18, color: "#059669" }} />
                  <Typography
                    component="a"
                    href="tel:08034660713"
                    variant="body2"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: 12, sm: 13 },
                      color: "#059669",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    08034660713
                  </Typography>
                </Stack>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Ethical Injunction Section (Quranic Covenant) */}
        <Container maxWidth="lg" sx={{ mt: -4, mb: 8, position: "relative", zIndex: 2 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              border: `1.5px solid ${alpha("#059669", 0.35)}`,
              background:
                theme.palette.mode === "dark"
                  ? "linear-gradient(135deg, rgba(5, 150, 105, 0.18) 0%, rgba(11, 59, 96, 0.4) 100%)"
                  : "linear-gradient(135deg, #ECFDF5 0%, #F0FDF4 50%, #EFF6FF 100%)",
              boxShadow: "0 16px 40px rgba(5, 150, 105, 0.12)",
              p: { xs: 3, sm: 4.5 },
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3} sx={{ textAlign: { xs: "center", md: "left" } }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 3,
                    bgcolor: alpha("#059669", 0.16),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#059669",
                    mb: 1.5,
                    mx: { xs: "auto", md: 0 },
                  }}
                >
                  <BalanceRoundedIcon sx={{ fontSize: 34 }} />
                </Box>
                <Typography variant="overline" sx={{ color: "#059669", fontWeight: 800, letterSpacing: 1.2 }}>
                  Islamic Covenant
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: theme.palette.mode === "dark" ? "#FFFFFF" : "#0B3B60" }}>
                  Qur'an 2:278–279
                </Typography>
              </Grid>

              <Grid item xs={12} md={9}>
                <Typography
                  sx={{
                    fontStyle: "italic",
                    fontSize: { xs: 14, sm: 16 },
                    lineHeight: 1.8,
                    color: theme.palette.mode === "dark" ? "#F1F5F9" : "#1E293B",
                    fontWeight: 500,
                    position: "relative",
                    pl: { xs: 0, sm: 2 },
                    borderLeft: { sm: `3px solid #059669` },
                  }}
                >
                  &ldquo;O you who have believed, fear Allah and give up what remains [due to you] of interest, if you should be believers. And if you do not, then be informed of a war from Allah and His Messenger...&rdquo;
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 1.5,
                    color: theme.palette.mode === "dark" ? "rgba(226, 232, 240, 0.7)" : "#475569",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  This divine injunction forms the sacred cornerstone of Mutual Trust CMS. Every savings ledger, loan disbursement, and business partnership is conducted with strict adherence to zero-usury, honesty, and mutual responsibility.
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </Container>

        {/* Milestones Timeline */}
        <Container maxWidth="lg" sx={{ mb: 10 }}>
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Chip
              label="CHRONOLOGY"
              sx={{
                fontWeight: 800,
                fontSize: 11,
                bgcolor: alpha("#0B3B60", 0.08),
                color: "#0B3B60",
                mb: 1.5,
                letterSpacing: 1,
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                fontSize: { xs: 24, sm: 34 },
                letterSpacing: -0.5,
                color: theme.palette.mode === "dark" ? "#FFFFFF" : "#0B3B60",
                mb: 1,
              }}
            >
              Key Milestones in Our Journey
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.mode === "dark" ? "rgba(226, 232, 240, 0.7)" : "#64748B",
                maxWidth: 600,
                mx: "auto",
              }}
            >
              From a small community circle in 2007 to a pioneering digital non-interest cooperative society.
            </Typography>
          </Box>

          <Box sx={{ position: "relative" }}>
            {/* Timeline Center Line for Desktop */}
            <Box
              sx={{
                position: "absolute",
                left: { xs: 28, md: "50%" },
                top: 24,
                bottom: 24,
                width: 3,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(11, 59, 96, 0.12)",
                transform: { md: "translateX(-50%)" },
                borderRadius: 2,
              }}
            />

            <Stack spacing={{ xs: 4, md: 6 }}>
              {milestones.map((m, idx) => {
                const isEven = idx % 2 === 0;
                return (
                  <Box
                    key={`${m.year}-${m.title}`}
                    sx={{
                      position: "relative",
                      display: "flex",
                      flexDirection: { xs: "column", md: isEven ? "row" : "row-reverse" },
                      alignItems: { xs: "flex-start", md: "center" },
                      gap: { xs: 2, md: 6 },
                    }}
                  >
                    {/* Content Card Side */}
                    <Box sx={{ flex: 1, pl: { xs: 8, md: 0 }, width: "100%" }}>
                      <Card
                        elevation={0}
                        sx={{
                          p: { xs: 3, sm: 3.5 },
                          borderRadius: 3.5,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.03)"
                              : "#FFFFFF",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.08)"
                              : "rgba(11, 59, 96, 0.08)"
                          }`,
                          boxShadow:
                            theme.palette.mode === "dark"
                              ? "0 10px 30px rgba(0, 0, 0, 0.3)"
                              : "0 10px 30px rgba(11, 59, 96, 0.05)",
                          transition: "all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow:
                              theme.palette.mode === "dark"
                                ? "0 18px 40px rgba(0, 0, 0, 0.5)"
                                : "0 18px 40px rgba(11, 59, 96, 0.12)",
                            borderColor: alpha(m.color, 0.4),
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mb: 1.5 }}
                        >
                          <Chip
                            label={m.category}
                            size="small"
                            sx={{
                              bgcolor: alpha(m.color, 0.12),
                              color: m.color,
                              fontWeight: 700,
                              fontSize: 11,
                              borderRadius: 1.5,
                            }}
                          />
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 900,
                              color: m.color,
                              fontSize: { xs: 16, sm: 18 },
                            }}
                          >
                            {m.year}
                          </Typography>
                        </Stack>

                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 800,
                            fontSize: { xs: 16, sm: 18 },
                            color: theme.palette.mode === "dark" ? "#FFFFFF" : "#0B3B60",
                            mb: 1,
                          }}
                        >
                          {m.title}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              theme.palette.mode === "dark"
                                ? "rgba(226, 232, 240, 0.8)"
                                : "#475569",
                            lineHeight: 1.7,
                            fontSize: { xs: 13, sm: 14 },
                            mb: 2,
                          }}
                        >
                          {m.desc}
                        </Typography>

                        <Stack spacing={0.75}>
                          {m.highlights.map((item) => (
                            <Stack
                              key={item}
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <CheckRoundedIcon
                                sx={{ fontSize: 16, color: m.color, flexShrink: 0 }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "rgba(226, 232, 240, 0.9)"
                                      : "#334155",
                                  fontSize: 12,
                                }}
                              >
                                {item}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>

                        {m.hasCertificate && (
                          <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px dashed ${alpha(m.color, 0.3)}` }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<VerifiedUserRoundedIcon />}
                              onClick={() => setCertOpen(true)}
                              sx={{
                                textTransform: "none",
                                fontWeight: 700,
                                fontSize: "0.8rem",
                                color: m.color,
                                borderColor: alpha(m.color, 0.5),
                                borderRadius: 2,
                                "&:hover": {
                                  borderColor: m.color,
                                  bgcolor: alpha(m.color, 0.08),
                                },
                              }}
                            >
                              View Certificate of Registration
                            </Button>
                          </Box>
                        )}
                      </Card>
                    </Box>

                    {/* Timeline Node Icon (Center on Desktop, Left on Mobile) */}
                    <Box
                      sx={{
                        position: { xs: "absolute", md: "relative" },
                        left: { xs: 8, md: "auto" },
                        top: { xs: 20, md: "auto" },
                        width: { xs: 44, md: 54 },
                        height: { xs: 44, md: 54 },
                        borderRadius: "50%",
                        bgcolor: m.color,
                        color: "#FFFFFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 0 0 6px ${
                          theme.palette.mode === "dark" ? "#061523" : "#F8FAFC"
                        }, 0 6px 20px ${alpha(m.color, 0.4)}`,
                        zIndex: 2,
                        flexShrink: 0,
                      }}
                    >
                      {m.icon}
                    </Box>

                    {/* Empty balancing side for desktop symmetry */}
                    <Box
                      sx={{
                        flex: 1,
                        display: { xs: "none", md: "block" },
                        textAlign: isEven ? "left" : "right",
                        px: 3,
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{
                          fontWeight: 900,
                          fontSize: 64,
                          color:
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.05)"
                              : "rgba(11, 59, 96, 0.06)",
                          lineHeight: 1,
                          userSelect: "none",
                        }}
                      >
                        {m.year}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Container>

        {/* 4 Guiding Pillars */}
        <Box
          sx={{
            py: { xs: 6, md: 9 },
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.02)"
                : alpha("#0B3B60", 0.02),
            borderTop: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.06)"
                : "rgba(11, 59, 96, 0.06)"
            }`,
            borderBottom: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.06)"
                : "rgba(11, 59, 96, 0.06)"
            }`,
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Chip
                label="FOUNDATIONAL PILLARS"
                sx={{
                  fontWeight: 800,
                  fontSize: 11,
                  bgcolor: alpha("#059669", 0.1),
                  color: "#059669",
                  mb: 1.5,
                  letterSpacing: 1,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: 24, sm: 34 },
                  letterSpacing: -0.5,
                  color: theme.palette.mode === "dark" ? "#FFFFFF" : "#0B3B60",
                  mb: 1,
                }}
              >
                The Four Pillars of Mutual Trust
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.mode === "dark" ? "rgba(226, 232, 240, 0.7)" : "#64748B",
                  maxWidth: 580,
                  mx: "auto",
                }}
              >
                Uncompromising values that have anchored our financial community for over twenty years.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {pillars.map((p) => (
                <Grid item xs={12} sm={6} md={3} key={p.title}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      p: 3,
                      borderRadius: 3.5,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.03)"
                          : "#FFFFFF",
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(11, 59, 96, 0.08)"
                      }`,
                      transition: "transform 0.25s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    <Box sx={{ mb: 2 }}>{p.icon}</Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        fontSize: 16,
                        color: theme.palette.mode === "dark" ? "#FFFFFF" : "#0B3B60",
                        mb: 1,
                      }}
                    >
                      {p.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? "rgba(226, 232, 240, 0.75)"
                            : "#64748B",
                        lineHeight: 1.6,
                        fontSize: 13,
                      }}
                    >
                      {p.desc}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Jaiz Bank Institutional Accounts Showcase */}
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
          <Box sx={{ textAlign: "center", mb: 5 }}>
            <Chip
              label="INSTITUTIONAL BANKING"
              sx={{
                fontWeight: 800,
                fontSize: 11,
                bgcolor: alpha("#0B3B60", 0.08),
                color: "#0B3B60",
                mb: 1.5,
                letterSpacing: 1,
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                fontSize: { xs: 24, sm: 34 },
                letterSpacing: -0.5,
                color: theme.palette.mode === "dark" ? "#FFFFFF" : "#0B3B60",
                mb: 1,
              }}
            >
              Official Jaiz Bank Accounts
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.mode === "dark" ? "rgba(226, 232, 240, 0.7)" : "#64748B",
                maxWidth: 620,
                mx: "auto",
              }}
            >
              All deposits, loan repayments, and share capital allocations are routed exclusively through our verified Jaiz Bank accounts.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {jaizAccounts.map((acct) => {
              const isCopied = copiedAccount === acct.number;
              return (
                <Grid item xs={12} sm={6} md={3} key={acct.number}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3.5,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.03)"
                          : "#FFFFFF",
                      border: `1.5px solid ${
                        isCopied
                          ? "#059669"
                          : theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(11, 59, 96, 0.08)"
                      }`,
                      transition: "all 0.25s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        borderColor: alpha("#059669", 0.4),
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 1 }}
                    >
                      <Chip
                        label={acct.category}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: 10,
                          bgcolor: alpha("#059669", 0.1),
                          color: "#059669",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 800,
                          color: "#0B3B60",
                          letterSpacing: 0.5,
                        }}
                      >
                        JAIZ BANK
                      </Typography>
                    </Stack>

                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.mode === "dark" ? "#FFFFFF" : "#0F172A",
                        minHeight: 42,
                        lineHeight: 1.3,
                        mb: 1.5,
                      }}
                    >
                      {acct.title}
                    </Typography>

                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        p: 1.5,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "#F1F5F9",
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: "monospace",
                          fontWeight: 800,
                          letterSpacing: 1.5,
                          color: theme.palette.mode === "dark" ? "#67E8F9" : "#0B3B60",
                          fontSize: 15,
                        }}
                      >
                        {acct.number}
                      </Typography>
                      <Tooltip title={isCopied ? "Copied!" : "Copy Account Number"}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(acct.number)}
                          sx={{
                            color: isCopied ? "#059669" : "text.secondary",
                            "&:hover": { color: "#059669" },
                          }}
                        >
                          {isCopied ? (
                            <CheckRoundedIcon fontSize="small" />
                          ) : (
                            <ContentCopyRoundedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Container>

        {/* CTA Banner */}
        <Box
          sx={{
            py: { xs: 6, md: 8 },
            bgcolor: "#0B3B60",
            color: "#FFFFFF",
            textAlign: "center",
          }}
        >
          <Container maxWidth="md">
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                fontSize: { xs: 24, sm: 32 },
                letterSpacing: -0.5,
                mb: 1.5,
              }}
            >
              Experience the Future of Ethical Finance
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                fontSize: { xs: 14, sm: 16 },
                mb: 4,
                maxWidth: 580,
                mx: "auto",
              }}
            >
              Whether you are an artisan, academic, civil servant, or entrepreneur, join over {communityStats.activeMembers}+ members building prosperous, non-interest futures.
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                endIcon={<LoginRoundedIcon />}
                sx={{
                  bgcolor: "#059669",
                  color: "#FFFFFF",
                  fontWeight: 800,
                  fontSize: 14,
                  px: 4,
                  py: 1.25,
                  borderRadius: 2.5,
                  boxShadow: "0 8px 24px rgba(5, 150, 105, 0.35)",
                  "&:hover": {
                    bgcolor: "#047857",
                  },
                }}
              >
                Access Member Portal
              </Button>
              <Button
                component={RouterLink}
                to="/"
                variant="outlined"
                startIcon={<HomeRoundedIcon />}
                sx={{
                  color: "#FFFFFF",
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  fontWeight: 700,
                  fontSize: 14,
                  px: 3.5,
                  py: 1.25,
                  borderRadius: 2.5,
                  "&:hover": {
                    borderColor: "#FFFFFF",
                    bgcolor: "rgba(255, 255, 255, 0.08)",
                  },
                }}
              >
                Back to Homepage
              </Button>
            </Stack>
          </Container>
        </Box>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            py: 4,
            borderTop: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.06)"
                : "rgba(11, 59, 96, 0.08)"
            }`,
            bgcolor: theme.palette.mode === "dark" ? "#040E18" : "#F1F5F9",
            textAlign: "center",
          }}
        >
          <Container maxWidth="lg">
            <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 13, mb: 1 }}>
              © 2007 — {new Date().getFullYear()} Mutual Trust Cooperative Multipurpose Society. All rights reserved.
            </Typography>
            <Typography variant="caption" sx={{ color: "text.disabled", fontSize: 11 }}>
              Permanent Site, Odofin Via Somorin Obantoko, Abeokuta, Ogun State | Phone: 08034660713
            </Typography>
          </Container>
        </Box>

        <ProtectedCertificateModal open={certOpen} onClose={() => setCertOpen(false)} />
      </Box>
    </>
  );
}
